import { supabase } from "@/shared/lib/supabase";
import { AUTH_ROUTES } from "@/shared/constants/paths";
import {
  IMPERSONATION_QUERY_PARAM,
  IMPERSONATION_STORAGE_KEY,
} from "@/shared/constants/impersonation";

export interface ImpersonationContext {
  admin_id?: string;
  admin_email: string;
  user_email: string;
  token: string;
}

interface ValidateResponse {
  valid: boolean;
  admin_email: string | null;
  user_email: string | null;
}

let cachedContext: ImpersonationContext | null | undefined;

function isImpersonationContext(value: unknown): value is ImpersonationContext {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as ImpersonationContext).admin_email === "string" &&
    typeof (value as ImpersonationContext).user_email === "string" &&
    typeof (value as ImpersonationContext).token === "string"
  );
}

export function getImpersonationContext(): ImpersonationContext | null {
  if (cachedContext !== undefined) {
    return cachedContext;
  }
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    cachedContext = isImpersonationContext(parsed) ? parsed : null;
  } catch {
    cachedContext = null;
  }
  return cachedContext;
}

function setImpersonationContext(context: ImpersonationContext): void {
  cachedContext = context;
  sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(context));
}

export function clearImpersonationContext(): void {
  cachedContext = null;
  sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
}

async function validateImpersonationToken(
  token: string,
  currentUserEmail: string,
): Promise<ImpersonationContext | null> {
  try {
    const { data, error } = await supabase.functions.invoke<ValidateResponse>(
      "impersonation/validate",
      { body: { token } },
    );

    if (
      error ||
      !data?.valid ||
      !data.user_email ||
      data.user_email.toLowerCase() !== currentUserEmail.toLowerCase()
    ) {
      return null;
    }

    return {
      admin_email: data.admin_email ?? "",
      user_email: data.user_email,
      token,
    };
  } catch {
    return null;
  }
}

function stripImpersonationParamFromUrl(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has(IMPERSONATION_QUERY_PARAM)) {
    url.searchParams.delete(IMPERSONATION_QUERY_PARAM);
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  }
}

export async function terminateImpersonation(): Promise<void> {
  const context = getImpersonationContext();

  if (context?.token) {
    try {
      await supabase.functions.invoke("impersonation/revoke", {
        body: { token: context.token },
      });
    } catch {
      // Fail-safe: a failed server-side revoke must never block ending the local session.
    }
  }

  try {
    await supabase.auth.signOut();
  } finally {
    clearImpersonationContext();
    window.location.href = AUTH_ROUTES.SIGN_IN;
  }
}

export async function bootstrapImpersonation(
  currentUserEmail: string | null | undefined,
): Promise<ImpersonationContext | null> {
  const token = new URLSearchParams(window.location.search).get(IMPERSONATION_QUERY_PARAM);

  if (!token || !currentUserEmail) {
    return getImpersonationContext();
  }

  const context = await validateImpersonationToken(token, currentUserEmail);
  if (context) {
    setImpersonationContext(context);
  }

  stripImpersonationParamFromUrl();

  return context;
}
